import type { RouteComponentProps } from '@reach/router';
import { navigate } from 'gatsby';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import RoundedButton from '@components/RoundedButton/index';
import ViewTab from '@components/ViewTab';

import { ADMIN_NEW_ACCOUNT } from '@constants/routes';

import {
  useGetAllPatientUsers,
  useGetAllNonPatientUsers,
} from '@hooks/api/user';
import { useSession } from '@hooks/useSession';

import { Spacer } from '@layouts/Layouts/BigTitleLayout';

import { getAccountRelationship } from '@models/client';
import type { Client } from '@models/client';

import { formatClientId } from '@utils/format';

import MainWrapper from '@views/Records/Common/MainWrapper';

import ClientList from './ClientList/ClientList';

const messages = defineMessages({
  title: {
    id: 'clientsMain.title',
    defaultMessage: 'Clients',
  },
  patients: {
    id: 'clientsMain.patients',
    defaultMessage: 'Patients',
  },
  nonPatients: {
    id: 'clientsMain.nonPatients',
    defaultMessage: 'Non Patients',
  },
  addClient: {
    id: 'clientsMain.addClient',
    defaultMessage: 'Add Client',
  },
});

const Clients: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const {
    state: { isCurator, isPractitioner },
  } = useSession();
  const { data: patientsData, loading: patientsLoading } =
    useGetAllPatientUsers();
  const { data: nonPatientsData, loading: nonPatientsLoading } =
    useGetAllNonPatientUsers({ skip: isPractitioner });

  const tabs = [
    {
      id: 'patients',
      value: formatMessage(messages.patients),
      count: (patientsData ?? []).length,
    },
    {
      id: 'nonPatients',
      value: formatMessage(messages.nonPatients),
      count: (nonPatientsData ?? []).length,
    },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const patientData = (patientsData ?? []).map<Client>(
    ({
      first_name,
      last_login,
      last_name,
      status,
      user_id,
      vitall_patient,
      vitall_user_id,
      group_name,
      account,
      parent_account,
      user_plan,
    }) => {
      const curator = {
        vitall_patient_id: vitall_patient?.vitall_patient_id,
        curator_user_id: vitall_patient?.curator?.user_id,
        first_name: vitall_patient?.curator?.first_name,
        last_name: vitall_patient?.curator?.last_name,
      };
      const practitioner = {
        vitall_patient_id: vitall_patient?.vitall_patient_id,
        practitioner_user_id: vitall_patient?.practitioner?.user_id,
        first_name: vitall_patient?.practitioner?.first_name,
        last_name: vitall_patient?.practitioner?.last_name,
      };
      return {
        vitall_id: formatClientId(vitall_user_id ?? '', true),
        user_id: user_id,
        name: `${last_name}, ${first_name}`,
        lastLogin: last_login,
        patient: vitall_patient,
        newRecords: vitall_patient?.recordMeta?.New ?? 0,
        reviewRecords: vitall_patient?.recordMeta?.NeedsReview ?? 0,
        draftRecords: vitall_patient?.recordMeta?.Draft ?? 0,
        publishedRecords: vitall_patient?.recordMeta?.Published ?? 0,
        vitall_patient_id: vitall_patient?.vitall_patient_id,
        fhirID: vitall_patient?.patient_id,
        userStatus: status,
        // patientStatus: vitall_patient?.status?.private_name,
        curator,
        group_name,
        practitioner,
        parent_account,
        plan: user_plan,
        accountRelationship: getAccountRelationship(account?.parent_id),
      };
    }
  );

  const nonPatientData = (nonPatientsData ?? []).map<Client>(
    ({
      first_name,
      last_login,
      last_name,
      status,
      user_id,
      vitall_user_id,
      group_name,
      account,
      parent_account,
    }) => ({
      vitall_id: formatClientId(vitall_user_id ?? '', true),
      user_id: user_id,
      name: `${last_name}, ${first_name}`,
      lastLogin: last_login,
      group_name,
      userStatus: status,
      parent_account,
      accountRelationship: getAccountRelationship(account?.parent_id),
    })
  );

  const title = formatMessage(
    isPractitioner ? messages.patients : messages.title
  );

  return (
    <MainWrapper
      friendlyName={title}
      loading={patientsLoading || nonPatientsLoading}
      title={title}
    >
      {isCurator ? (
        <>
          <RoundedButton
            autoSize
            iconName="plus"
            onClick={() => {
              navigate(ADMIN_NEW_ACCOUNT);
            }}
          >
            {messages.addClient.defaultMessage}
          </RoundedButton>
          <Spacer />
          <ViewTab
            changeTab={setActiveTab}
            selected={activeTab}
            tabArray={tabs}
          />
        </>
      ) : null}

      <ClientList
        data={activeTab.id === 'patients' ? patientData : nonPatientData}
        type={activeTab.value.toUpperCase()}
      />
    </MainWrapper>
  );
};

export default Clients;
